import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { CheckCircle, XCircle, Calendar, TrendingUp, BarChart2 } from "lucide-react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function getYearRange() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = current; y >= current - 4; y--) years.push(y);
  return years;
}

export default function Attendance() {
  const { currentUser } = useAuth();
  const [records,       setRecords]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear,  setSelectedYear]  = useState(new Date().getFullYear());
  const [activeTab,     setActiveTab]     = useState("calendar"); // calendar | events | graph

  const YEARS = getYearRange();

  useEffect(() => {
  async function load() {
    try {
      // Simple query without orderBy - avoids needing composite index
      const q = query(
        collection(db, "attendance"),
        where("userId", "==", currentUser.uid)
      );
      const snap = await getDocs(q);
      const data = snap.docs
        .map(d => ({ id:d.id, ...d.data() }))
        // Sort client-side by date descending
        .sort((a, b) => {
          const da = a.date ? new Date(a.date) : new Date(0);
          const db2 = b.date ? new Date(b.date) : new Date(0);
          return db2 - da;
        });
      setRecords(data);
    } catch(e) {
      console.error("Attendance load error:", e);
    } finally {
      setLoading(false);
    }
  }
  if (currentUser) load();
}, [currentUser]);

  // ── Stats ──
  const present      = records.filter(r => r.status === "present").length;
  const absent       = records.filter(r => r.status === "absent").length;
  const total        = records.length;
  const overallPct   = total > 0 ? Math.round((present/total)*100) : 0;

  // ── Filter by selected month + year ──
  const monthRecords = records.filter(r => {
    if (!r.date) return false;
    const d = new Date(r.date);
    return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
  });
  const monthPresent = monthRecords.filter(r => r.status === "present").length;
  const monthTotal   = monthRecords.length;
  const monthPct     = monthTotal > 0 ? Math.round((monthPresent/monthTotal)*100) : 0;

  // ── Calendar grid ──
  const firstDay     = new Date(selectedYear, selectedMonth, 1).getDay();
  const daysInMonth  = new Date(selectedYear, selectedMonth+1, 0).getDate();
  const isCurrentMonth = selectedMonth === new Date().getMonth() && selectedYear === new Date().getFullYear();
  const today        = new Date().getDate();

  const dateMap = {};
  monthRecords.forEach(r => {
    if (r.date) dateMap[new Date(r.date).getDate()] = r;
  });

  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calDays.push(i);

  // ── Yearly monthly summary for graph ──
  const yearlyData = MONTHS.map((m, mi) => {
    const recs = records.filter(r => {
      if (!r.date) return false;
      const d = new Date(r.date);
      return d.getMonth() === mi && d.getFullYear() === selectedYear;
    });
    const p = recs.filter(r => r.status === "present").length;
    const t = recs.length;
    return { month:m, present:p, total:t, pct: t > 0 ? Math.round((p/t)*100) : 0 };
  });

  // ── Event-wise records ──
  const eventRecords = records.filter(r => {
    if (!r.date) return false;
    const d = new Date(r.date);
    return d.getFullYear() === selectedYear;
  });

  // Group by event
  const eventMap = {};
  eventRecords.forEach(r => {
    const key = r.eventId || r.eventName || "Unknown Event";
    if (!eventMap[key]) eventMap[key] = { name: r.eventName || key, date: r.date, status: r.status };
    else eventMap[key] = { ...eventMap[key], status: r.status };
  });
  const eventList = Object.values(eventMap);

  return (
    <div style={{ maxWidth:860 }}>

      {/* ── Stats Row ── */}
      <div style={s.statsRow}>
        {[
          { label:"This Month",  value:`${monthPct}%`,  sub:`${monthPresent}/${monthTotal} events`,  color:"#22c55e", icon:TrendingUp },
          { label:"Overall Rate",value:`${overallPct}%`,sub:`${present} of ${total} total`,          color:"#3b82f6", icon:Calendar },
          { label:"Present",     value:present,         sub:"events attended",                       color:"#a855f7", icon:CheckCircle },
          { label:"Absent",      value:absent,          sub:"events missed",                         color:"#ef4444", icon:XCircle },
        ].map(({ label, value, sub, color, icon:Icon }) => (
          <div key={label} style={s.statCard}>
            <div style={{ ...s.statIcon, background:`${color}18` }}><Icon size={18} color={color}/></div>
            <div style={{ ...s.statVal, color }}>{value}</div>
            <div style={s.statLabel}>{label}</div>
            <div style={s.statSub}>{sub}</div>
          </div>
        ))}
      </div>

      {/* ── Year + Month Filters ── */}
      <div style={s.filterBar}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <Calendar size={15} color="#f59e0b"/>
          <span style={{ fontSize:13, fontWeight:700, color:"#fef3e2" }}>Filter by:</span>
        </div>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          {/* Year */}
          <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} style={s.select}>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {/* Month */}
          <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))} style={s.select}>
            {MONTHS.map((m,i) => <option key={m} value={i}>{m}</option>)}
          </select>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", gap:6, marginLeft:"auto", flexWrap:"wrap" }}>
          {[
            { id:"calendar", label:"Calendar",  icon:Calendar },
            { id:"events",   label:"By Events", icon:CheckCircle },
            { id:"graph",    label:"Graph",      icon:BarChart2 },
          ].map(({ id, label, icon:Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{
              display:"flex", alignItems:"center", gap:6,
              padding:"7px 14px", borderRadius:8, border:"1px solid",
              borderColor: activeTab===id ? "#f59e0b" : "rgba(255,255,255,0.08)",
              background:  activeTab===id ? "rgba(245,158,11,0.12)" : "transparent",
              color:       activeTab===id ? "#f59e0b" : "#78716c",
              fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
            }}>
              <Icon size={13}/>{label}
            </button>
          ))}
        </div>
      </div>

      {/* ── CALENDAR TAB ── */}
      {activeTab === "calendar" && (
        <div style={s.card}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:8 }}>
            <div style={s.cardTitle}>
              <Calendar size={15} color="#f59e0b"/>
              {MONTHS[selectedMonth]} {selectedYear}
            </div>
            <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
              {[["#22c55e","Present"],["#ef4444","Absent"],["rgba(255,255,255,0.06)","No Event"]].map(([c,l]) => (
                <div key={l} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"#78716c" }}>
                  <div style={{ width:10, height:10, borderRadius:3, background:c }}/>{l}
                </div>
              ))}
              {isCurrentMonth && <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:"#78716c" }}><div style={{ width:10, height:10, borderRadius:3, border:"1.5px solid #f59e0b" }}/> Today</div>}
            </div>
          </div>

          {/* Day headers */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:5, marginBottom:6 }}>
            {DAYS.map(d => <div key={d} style={{ textAlign:"center", fontSize:11, color:"#52504d", fontWeight:700 }}>{d}</div>)}
          </div>

          {/* Calendar grid */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:5 }}>
            {calDays.map((day, i) => {
              if (!day) return <div key={`empty-${i}`}/>;
              const rec    = dateMap[day];
              const isToday = isCurrentMonth && day === today;
              let bg    = "rgba(255,255,255,0.03)";
              let color = "#52504d";
              if (rec?.status === "present") { bg = "rgba(34,197,94,0.18)";  color = "#22c55e"; }
              if (rec?.status === "absent")  { bg = "rgba(239,68,68,0.18)";  color = "#ef4444"; }
              return (
                <div key={day} title={rec ? `${rec.eventName || "Event"} — ${rec.status}` : ""} style={{
                  aspectRatio:"1", display:"flex", alignItems:"center", justifyContent:"center",
                  borderRadius:8, fontSize:12, fontWeight:rec?700:400,
                  background:bg, color,
                  border: isToday ? "1.5px solid #f59e0b" : "1px solid transparent",
                  cursor: rec ? "pointer" : "default", position:"relative",
                }}>
                  {day}
                  {rec?.status === "present" && <div style={{ position:"absolute", bottom:3, left:"50%", transform:"translateX(-50%)", width:4, height:4, borderRadius:"50%", background:"#22c55e" }}/>}
                </div>
              );
            })}
          </div>

          {/* Month summary */}
          <div style={{ marginTop:16, padding:"12px 14px", background:"rgba(255,255,255,0.02)", borderRadius:9, display:"flex", gap:24, flexWrap:"wrap" }}>
            <span style={{ fontSize:12, color:"#22c55e", fontWeight:700 }}>✓ {monthPresent} Present</span>
            <span style={{ fontSize:12, color:"#ef4444", fontWeight:700 }}>✗ {monthTotal - monthPresent} Absent</span>
            <span style={{ fontSize:12, color:"#f59e0b", fontWeight:700 }}>📊 {monthPct}% Rate</span>
          </div>
        </div>
      )}

      {/* ── EVENTS TAB ── */}
      {activeTab === "events" && (
        <div style={s.card}>
          <div style={{ ...s.cardTitle, marginBottom:16 }}>
            <CheckCircle size={15} color="#f59e0b"/> Event-wise Attendance — {selectedYear}
          </div>
          {eventList.length === 0 ? (
            <div style={s.empty}>No events found for {selectedYear}</div>
          ) : eventList.map((evt, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
              <div style={{
                width:36, height:36, borderRadius:9, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
                background: evt.status==="present" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
              }}>
                {evt.status === "present"
                  ? <CheckCircle size={18} color="#22c55e"/>
                  : <XCircle    size={18} color="#ef4444"/>}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, color:"#fef3e2" }}>{evt.name}</div>
                <div style={{ fontSize:11, color:"#52504d", marginTop:2 }}>{evt.date}</div>
              </div>
              <span style={{
                fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:50,
                background: evt.status==="present" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)",
                color: evt.status==="present" ? "#22c55e" : "#ef4444",
              }}>
                {evt.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── GRAPH TAB ── */}
      {activeTab === "graph" && (
        <div style={s.card}>
          <div style={{ ...s.cardTitle, marginBottom:20 }}>
            <BarChart2 size={15} color="#f59e0b"/> Monthly Attendance Graph — {selectedYear}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {yearlyData.map(({ month, present, total, pct }) => (
              <div key={month} style={{ display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ fontSize:12, color:"#78716c", width:32, flexShrink:0 }}>{month}</div>
                <div style={{ flex:1, height:28, background:"rgba(255,255,255,0.04)", borderRadius:6, overflow:"hidden", position:"relative" }}>
                  <div style={{
                    height:"100%",
                    width: total > 0 ? `${pct}%` : "0%",
                    background: pct >= 80 ? "linear-gradient(90deg,#22c55e,#16a34a)" : pct >= 50 ? "linear-gradient(90deg,#f59e0b,#d97706)" : pct > 0 ? "linear-gradient(90deg,#ef4444,#dc2626)" : "transparent",
                    borderRadius:6,
                    transition:"width 0.8s ease",
                    display:"flex", alignItems:"center", paddingLeft:8,
                  }}>
                    {pct > 15 && <span style={{ fontSize:11, fontWeight:800, color:"#fff" }}>{pct}%</span>}
                  </div>
                </div>
                <div style={{ fontSize:12, color:"#52504d", width:70, flexShrink:0, textAlign:"right" }}>
                  {total > 0 ? `${present}/${total} events` : "No events"}
                </div>
              </div>
            ))}
          </div>

          {/* Color legend */}
          <div style={{ marginTop:20, display:"flex", gap:16, flexWrap:"wrap" }}>
            {[["#22c55e","80%+ (Excellent)"],["#f59e0b","50–79% (Good)"],["#ef4444","Below 50% (Needs Improvement)"]].map(([c,l]) => (
              <div key={l} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#78716c" }}>
                <div style={{ width:12, height:12, borderRadius:3, background:c }}/>{l}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Recent History ── */}
      <div style={s.card}>
        <div style={{ ...s.cardTitle, marginBottom:14 }}>Recent Attendance History</div>
        {loading ? <div style={s.empty}>Loading...</div> :
         records.length === 0 ? <div style={s.empty}>No attendance records yet</div> :
         records.slice(0, 10).map(r => (
          <div key={r.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
            <div style={{ ...s.statusIcon, background: r.status==="present" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)" }}>
              {r.status==="present" ? <CheckCircle size={16} color="#22c55e"/> : <XCircle size={16} color="#ef4444"/>}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:600, color:"#fef3e2" }}>{r.eventName || "Event"}</div>
              <div style={{ fontSize:11, color:"#52504d", marginTop:2 }}>{r.date}</div>
            </div>
            <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:50, background:r.status==="present"?"rgba(34,197,94,0.12)":"rgba(239,68,68,0.12)", color:r.status==="present"?"#22c55e":"#ef4444", textTransform:"capitalize" }}>
              {r.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const s = {
  statsRow:   { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:12, marginBottom:16 },
  statCard:   { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:16 },
  statIcon:   { width:36, height:36, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10 },
  statVal:    { fontSize:24, fontWeight:800, fontFamily:"'Playfair Display',serif", lineHeight:1, marginBottom:4 },
  statLabel:  { fontSize:12, fontWeight:700, color:"#fef3e2", marginBottom:2 },
  statSub:    { fontSize:11, color:"#52504d" },
  filterBar:  { display:"flex", alignItems:"center", gap:10, background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:14, marginBottom:14, flexWrap:"wrap" },
  select:     { background:"#1a1714", border:"1px solid rgba(255,255,255,0.1)", color:"#fef3e2", borderRadius:8, padding:"6px 12px", fontSize:12, fontFamily:"inherit", cursor:"pointer" },
  card:       { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20, marginBottom:16 },
  cardTitle:  { fontSize:14, fontWeight:700, color:"#fef3e2", display:"flex", alignItems:"center", gap:8 },
  statusIcon: { width:34, height:34, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  empty:      { fontSize:13, color:"#52504d", textAlign:"center", padding:"24px 0" },
};